import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

export interface ConfigSelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
}

interface ConfigSelectProps {
  id: string;
  value: string;
  options: ConfigSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  keepOpenOnSelect?: boolean;
  selectedValues?: Set<string> | string[];
  valueLabel?: string;
}

const PARENT_OPEN_CLASS = 'form-group-config--select-open';

export const ConfigSelect = ({
  id,
  value,
  options,
  placeholder,
  disabled,
  onChange,
  keepOpenOnSelect = false,
  selectedValues,
  valueLabel,
}: ConfigSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(() => options.findIndex((option) => option.value === value));
  const [isKeyboardNav, setIsKeyboardNav] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listboxId = `${id}-listbox`;
  const selectedValuesSet = useMemo(() => {
    if (!selectedValues) {
      return null;
    }
    return selectedValues instanceof Set ? selectedValues : new Set(selectedValues);
  }, [selectedValues]);
  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    setHighlightedIndex(options.findIndex((option) => option.value === value));
  }, [options, value]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsKeyboardNav(false);
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    const parentGroup = containerRef.current?.closest('.form-group-config');
    if (!parentGroup) {
      return;
    }
    if (isOpen) {
      parentGroup.classList.add(PARENT_OPEN_CLASS);
    } else {
      parentGroup.classList.remove(PARENT_OPEN_CLASS);
    }
    return () => {
      parentGroup.classList.remove(PARENT_OPEN_CLASS);
    };
  }, [isOpen]);

  const moveHighlight = (direction: 1 | -1) => {
    if (options.length === 0) {
      return;
    }
    setIsKeyboardNav(true);
    setHighlightedIndex((current) => {
      if (current === -1) {
        return direction === 1 ? 0 : options.length - 1;
      }
      return (current + direction + options.length) % options.length;
    });
  };

  const commitHighlightedOption = () => {
    if (highlightedIndex >= 0 && highlightedIndex < options.length) {
      const option = options[highlightedIndex];
      onChange(option.value);
      if (!keepOpenOnSelect) {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    }
  };

  const handleToggleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }
    const confirmSelection = () => {
      event.preventDefault();
      if (isOpen) {
        commitHighlightedOption();
      } else {
        setIsKeyboardNav(true);
        setIsOpen(true);
      }
    };
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        }
        moveHighlight(1);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        }
        moveHighlight(-1);
        break;
      }
      case 'Enter': {
        confirmSelection();
        break;
      }
      case ' ': {
        confirmSelection();
        break;
      }
      case 'Escape': {
        if (isOpen) {
          event.preventDefault();
          setIsKeyboardNav(false);
          setIsOpen(false);
        }
        break;
      }
      case 'Tab': {
        setIsKeyboardNav(false);
        setIsOpen(false);
        break;
      }
      default: {
        break;
      }
    }
  };

  const handleOptionSelect = (optionValue: string) => {
    onChange(optionValue);
    if (!keepOpenOnSelect) {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
    setIsKeyboardNav(false);
  };

  return (
    <div
      ref={containerRef}
      className={`config-select ${isOpen ? 'config-select--open' : ''} ${disabled ? 'config-select--disabled' : ''} ${
        isKeyboardNav ? 'config-select--keyboard-nav' : ''
      }`}
    >
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className="config-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        onClick={() => {
          if (disabled) {
            return;
          }
          setIsKeyboardNav(false);
          setIsOpen((prev) => !prev);
        }}
        onKeyDown={handleToggleKeyDown}
        disabled={disabled}
      >
        {selectedOption ? (
          <span className="config-select__value">
            {selectedOption.icon ? (
              <span className="config-select__icon" aria-hidden>
                {selectedOption.icon}
              </span>
            ) : null}
            <span className="config-select__label">
              {selectedOption.label}
              {selectedOption.description ? (
                <span className="config-select__description">{selectedOption.description}</span>
              ) : null}
            </span>
          </span>
        ) : valueLabel ? (
          <span className="config-select__value">
            <span className="config-select__label">{valueLabel}</span>
          </span>
        ) : (
          <span className="config-select__placeholder">{placeholder}</span>
        )}
        <span className="config-select__chevron" aria-hidden />
      </button>

      <ul
        id={listboxId}
        role="listbox"
        className="config-select__list"
        tabIndex={-1}
        hidden={!isOpen}
        aria-activedescendant={highlightedIndex >= 0 ? `${id}-option-${options[highlightedIndex].value}` : undefined}
      >
        {options.map((option, index) => {
          const isSelected = selectedValuesSet ? selectedValuesSet.has(option.value) : option.value === value;
          const isHighlighted = index === highlightedIndex;
          return (
            <li
              id={`${id}-option-${option.value}`}
              key={option.value}
              role="option"
              aria-selected={isSelected}
              className={`config-select__option ${isSelected ? 'config-select__option--selected' : ''} ${
                isHighlighted ? 'config-select__option--highlighted' : ''
              }`}
            >
              <button
                type="button"
                onMouseEnter={() => {
                  setIsKeyboardNav(false);
                  setHighlightedIndex(index);
                }}
                onFocus={() => {
                  setIsKeyboardNav(true);
                  setHighlightedIndex(index);
                }}
                onClick={() => handleOptionSelect(option.value)}
                className="config-select__option-button"
              >
                {option.icon ? (
                  <span className="config-select__icon" aria-hidden>
                    {option.icon}
                  </span>
                ) : null}
                <span className="config-select__label">
                  {option.label}
                  {option.description ? (
                    <span className="config-select__description">{option.description}</span>
                  ) : null}
                </span>
                {isSelected ? (
                  <span className="config-select__check" aria-hidden>
                    <span className="config-select__check-dot" />
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
